import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Building2,
  User,
  Mail,
  Phone,
  Loader2,
  Ban,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  CONFIRMED: 'bg-green-100 text-green-700 border-green-200',
  CANCELLED: 'bg-red-100 text-red-700 border-red-200',
  REFUNDED: 'bg-pink-100 text-pink-700 border-pink-200',
};

const statusIcons: Record<string, React.ReactNode> = {
  PENDING: <Clock className="h-4 w-4" />,
  CONFIRMED: <CheckCircle className="h-4 w-4" />,
  CANCELLED: <Ban className="h-4 w-4" />,
  REFUNDED: <Ban className="h-4 w-4" />,
};

export function HotelBookingDetailPage() {
  const { t } = useTranslation('hotels');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const { data: booking, isLoading, error, refetch } = trpc.hotel.getHotelBooking.useQuery(id!, {
    enabled: !!id,
  });

  const utils = trpc.useUtils();

  const cancelMutation = trpc.hotel.cancelHotelBooking.useMutation({
    onSuccess: () => {
      showSuccessToast('Hotel booking cancelled successfully');
      setShowCancelDialog(false);
      refetch();
      utils.hotel.listHotelBookings.invalidate();
    },
    onError: (error) => {
      showErrorToast(error.message || 'Failed to cancel booking');
    },
  });

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatShortDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const calculateNights = () => {
    if (!booking) return 0;
    const checkIn = new Date(booking.checkInDate);
    const checkOut = new Date(booking.checkOutDate);
    return Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{t('loadingBookingDetails')}</span>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Building2 className="h-12 w-12 text-muted-foreground/50" />
        <h3 className="mt-4 text-lg font-medium">{t('bookingNotFound')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {error?.message || t('bookingNotExist')}
        </p>
        <Button className="mt-4" onClick={() => navigate('/travel/hotels')}>
          {t('backToBookings')}
        </Button>
      </div>
    );
  }

  const nights = calculateNights();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/travel/hotels')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">
                {booking.bookingItem?.booking?.bookingReference || booking.amadeusBookingId}
              </h1>
              <span
                className={`inline-flex items-center gap-1 border px-2 py-0.5 text-xs font-medium ${
                  statusColors[booking.status] || 'bg-gray-100 text-gray-700'
                }`}
              >
                {statusIcons[booking.status]}
                {booking.status}
              </span>
            </div>
            <p className="text-muted-foreground">{t('hotelBookingDetails')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {booking.status === 'CONFIRMED' && (
            <Button
              variant="destructive"
              onClick={() => setShowCancelDialog(true)}
            >
              <Ban className="mr-2 h-4 w-4" />
              {t('cancelBooking')}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Hotel Information */}
          <div className="border border-border bg-card p-6">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              {t('hotelInformation')}
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">{booking.hotelName}</h3>
                <p className="text-sm text-muted-foreground">{t('hotelId')}{booking.hotelId}</p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">{t('checkIn')}</p>
                  <p className="font-medium">{formatDate(booking.checkInDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('checkOut')}</p>
                  <p className="font-medium">{formatDate(booking.checkOutDate)}</p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-sm text-muted-foreground">{t('duration')}</p>
                  <p className="font-medium">{nights} {t('nights')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('roomsLabel')}</p>
                  <p className="font-medium">{booking.roomCount}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('guestsLabel')}</p>
                  <p className="font-medium">{booking.guestCount}</p>
                </div>
              </div>
              {booking.roomType && (
                <div>
                  <p className="text-sm text-muted-foreground">{t('roomType')}</p>
                  <p className="font-medium">{booking.roomType}</p>
                </div>
              )}
              {booking.boardType && (
                <div>
                  <p className="text-sm text-muted-foreground">{t('boardType')}</p>
                  <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 text-sm">
                    {booking.boardType.replace('_', ' ')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Customer Information */}
          <div className="border border-border bg-card p-6">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              {t('customerInformation')}
            </h2>
            {booking.bookingItem?.booking?.customer && (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center bg-primary/10">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">
                      {booking.bookingItem.booking.customer.firstName}{' '}
                      {booking.bookingItem.booking.customer.lastName}
                    </p>
                    <Link
                      to={`/travel/cari/accounts/customer/${booking.bookingItem.booking.customer.id}`}
                      className="text-sm text-primary hover:underline"
                    >
                      {t('viewCustomerProfile')}
                    </Link>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{booking.bookingItem.booking.customer.email}</span>
                  </div>
                  {booking.bookingItem.booking.customer.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{booking.bookingItem.booking.customer.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Guest Details */}
          {booking.guestDetails && (
            <div className="border border-border bg-card p-6">
              <h2 className="font-semibold mb-4">{t('guestDetails')}</h2>
              <div className="space-y-3">
                {(booking.guestDetails as any[]).map((guest: any, index: number) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-muted/50">
                    <div className="flex h-8 w-8 items-center justify-center bg-primary/10 text-sm font-medium">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">
                        {guest.name?.title} {guest.name?.firstName} {guest.name?.lastName}
                      </p>
                      {guest.contact?.email && (
                        <p className="text-sm text-muted-foreground">{guest.contact.email}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Pricing Summary */}
          <div className="border border-border bg-card p-6">
            <h2 className="font-semibold mb-4">{t('pricingSummary')}</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('basePrice')}</span>
                <span>{booking.currency} {Number(booking.basePrice).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('taxes')}</span>
                <span>{booking.currency} {Number(booking.taxes).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm border-t pt-3">
                <span className="text-muted-foreground">{t('amadeusTotal')}</span>
                <span>{booking.currency} {Number(booking.totalAmadeusPrice).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('markup')}</span>
                <span className="text-green-600">+{booking.currency} {Number(booking.markupAmount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t pt-3">
                <span>{t('sellingPrice')}</span>
                <span>{booking.currency} {Number(booking.sellingPrice).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Booking Info */}
          <div className="border border-border bg-card p-6">
            <h2 className="font-semibold mb-4">{t('bookingInfo')}</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('amadeusId')}</span>
                <span className="font-mono">{booking.amadeusBookingId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('created')}</span>
                <span>{formatShortDate(booking.createdAt)}</span>
              </div>
              {booking.cancelledAt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('cancelled')}</span>
                  <span>{formatShortDate(booking.cancelledAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="border border-border bg-card p-6">
            <h2 className="font-semibold mb-4">{t('quickActions')}</h2>
            <div className="space-y-2">
              {booking.bookingItem?.booking?.id && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate(`/travel/bookings/${booking.bookingItem?.booking?.id}`)}
                >
                  {t('viewParentBooking')}
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/travel/hotels')}
              >
                {t('backToBookings')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('cancelHotelBooking')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('cancelConfirmation')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">{t('cancellationReason')}</label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder={t('cancellationPlaceholder')}
              className="mt-2 w-full border border-border p-3 text-sm resize-none"
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('keepBooking')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                cancelMutation.mutate({
                  hotelBookingId: booking.id,
                  reason: cancelReason || undefined,
                });
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {cancelMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('cancelBooking')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
